import React, { useCallback, useContext, useMemo, useState } from "react";
import { Button } from "antd";
import { Tooltip, useMediaQuery } from "@mui/material";
import { DownlineIcon } from "../../../../../../../../Components/Icons/downlineIcon";
import { ResendIcon } from "../../../../../../../../Components/Icons/resendIcon";
import { UserContext, UserPayload } from "../../../../../../../../Context/userContext";
import {
    AGENT_ACTION_BUTTON_TOOLTIPS,
    AGENT_ACTION_ICON_COLORS,
} from "../../../../../../agents.constants";
import { useAgentsStore } from "../../../../../../store/agents.store";
import {
    selectAgentDetail,
    selectHiddenFields,
    selectRestrictedFields,
} from "../../../../../../store/agentDetail/agentDetailSelectors";
import { CreateDownlineModal } from "../../../../../createDownlineModal/createDownlineModal.index";
import { getAgentDetailActionButtonState } from "./actionButtons.utils";
import styles from "./actionButtons.module.css";

interface AgentDetailActionButtonsProps {
    isSaving: boolean;
}

export const AgentDetailActionButtons = ({ isSaving }: AgentDetailActionButtonsProps) => {
    const { setShowLoader } = useContext(UserContext) as UserPayload;
    const hiddenFields = useAgentsStore(selectHiddenFields);
    const restrictedFields = useAgentsStore(selectRestrictedFields);
    const agentDetail = useAgentsStore(selectAgentDetail);
    const activeUserId = useAgentsStore((state) => state.detail.activeUserId);
    const fetchAgentDetails = useAgentsStore((state) => state.fetchAgentDetails);
    const resendInviteToAgent = useAgentsStore((state) => state.resendInviteToAgent);

    const [downlineModalOpen, setDownlineModalOpen] = useState(false);

    const { showCreateDownline, disableCreateDownline, showResendInvite } = useMemo(
        () =>
            getAgentDetailActionButtonState(
                hiddenFields.actionButtons,
                restrictedFields.actionButtons
            ),
        [hiddenFields.actionButtons, restrictedFields.actionButtons]
    );

    const email = agentDetail?.personalInformation.email?.trim() ?? "";
    const hasEmail = email.length > 0;
    const selectedUserId = activeUserId ?? agentDetail?.personalInformation.userId ?? null;

    const isCreateDisabled = disableCreateDownline || isSaving || selectedUserId == null;
    const isResendDisabled = !hasEmail || isSaving;

    const handleCreateDownline = useCallback(() => {
        if (isCreateDisabled || !selectedUserId) return;
        setDownlineModalOpen(true);
    }, [isCreateDisabled, selectedUserId]);

    const handleCloseDownlineModal = useCallback(() => {
        setDownlineModalOpen(false);
    }, []);

    const handleDownlineCreated = useCallback(async () => {
        if (selectedUserId == null) return;
        await fetchAgentDetails(selectedUserId, { silent: true });
    }, [fetchAgentDetails, selectedUserId]);

    const handleResendInvite = useCallback(() => {
        if (!hasEmail || isSaving) return;
        resendInviteToAgent(email, { setShowLoader });
    }, [email, hasEmail, isSaving, resendInviteToAgent, setShowLoader]);

    const isIconOnly = useMediaQuery("(max-width: 768px)");

    if (!showCreateDownline && !showResendInvite) return null;

    return (
        <>
            <div className={styles.buttonsCtn}>
                {showCreateDownline && (
                    <Tooltip
                        title={
                            disableCreateDownline
                                ? AGENT_ACTION_BUTTON_TOOLTIPS.CREATE_DOWNLINE_DISABLED
                                : (isIconOnly && AGENT_ACTION_BUTTON_TOOLTIPS.CREATE_DOWNLINE)
                        }
                        arrow
                    >
                        <span>
                            <Button
                                className={styles.actionButton}
                                aria-label={AGENT_ACTION_BUTTON_TOOLTIPS.CREATE_DOWNLINE}
                                icon={
                                    <DownlineIcon
                                        width={18}
                                        height={14}
                                        className={styles.actionIcon}
                                        fillColor={
                                            isCreateDisabled
                                                ? AGENT_ACTION_ICON_COLORS.DISABLED
                                                : AGENT_ACTION_ICON_COLORS.ENABLED
                                        }
                                    />
                                }
                                disabled={isCreateDisabled}
                                onClick={handleCreateDownline}
                            >
                                <span className={styles.label}>
                                    {AGENT_ACTION_BUTTON_TOOLTIPS.CREATE_DOWNLINE}
                                </span>
                            </Button>
                        </span>
                    </Tooltip>
                )}
                {showResendInvite && (
                    <Tooltip title={isIconOnly && AGENT_ACTION_BUTTON_TOOLTIPS.RESEND_INVITE} arrow>
                        <span>
                            <Button
                                className={styles.actionButton}
                                aria-label={AGENT_ACTION_BUTTON_TOOLTIPS.RESEND_INVITE}
                                icon={
                                    <ResendIcon
                                        width={18}
                                        height={14}
                                        className={styles.actionIcon}
                                        fillColor={
                                            isResendDisabled
                                                ? AGENT_ACTION_ICON_COLORS.DISABLED
                                                : AGENT_ACTION_ICON_COLORS.ENABLED
                                        }
                                    />
                                }
                                disabled={isResendDisabled}
                                onClick={handleResendInvite}
                            >
                                <span className={styles.label}>
                                    {AGENT_ACTION_BUTTON_TOOLTIPS.RESEND_INVITE}
                                </span>
                            </Button>
                        </span>
                    </Tooltip>
                )}
            </div>
            {showCreateDownline && (
                <CreateDownlineModal
                    open={downlineModalOpen}
                    onClose={handleCloseDownlineModal}
                    selectedUserId={selectedUserId}
                    onSuccess={handleDownlineCreated}
                />
            )}
        </>
    );
};
